import { useEffect, useMemo, useState } from 'react'

import { Box, useThemeProps } from '@mui/material'

import useToolbarInfo from '../../hooks/useToolbarInfo'
import theme from '../../theme/theme.mui'
import type { PageLayoutProps } from './PageLayout.types'
import PageLayoutContainer from './PageLayoutContainer'
import { PageLayoutRoot } from './PageLayout.styles'

const PageLayout = (inProps: PageLayoutProps) => {
  const props = useThemeProps({ props: inProps, name: 'PageLayout' })
  const { shouldRenderGhost, height: toolbarHeight } = useToolbarInfo()
  const {
    variant,
    maxWidth,
    children,
    warningChildren,
    sidebar,
    sidebarOpen,
    sidebarWidth,
    sidebarCollapsedWidth,
    containerProps,
    ...boxProps
  } = props

  // Content should not transition on mount if the sidebar is already open
  const [shouldTransition, setShouldTransition] = useState(!sidebarOpen)

  useEffect(() => {
    if (shouldTransition) return

    const timeout = setTimeout(() => setShouldTransition(true), theme.transitions.duration.enteringScreen)

    return () => clearTimeout(timeout)
  }, [shouldTransition])

  const containerMaxWidth = useMemo(() => {
    if (variant === 'two-column' || variant === 'two-column-with-sidebar') {
      return 'lg'
    } else if (maxWidth) {
      return maxWidth
    } else {
      return false
    }
  }, [maxWidth, variant])

  return (
    <>
      <PageLayoutRoot
        component="main"
        variant={variant}
        sidebarOpen={sidebarOpen}
        sidebarWidth={sidebarWidth}
        sidebarCollapsedWidth={sidebarCollapsedWidth}
        shouldTransition={shouldTransition}
        shouldRenderGhost={shouldRenderGhost}
        {...boxProps}
      >
        <Box mt={shouldRenderGhost ? toolbarHeight : 0}>
          <Box>{warningChildren}</Box>
          <PageLayoutContainer
            variant={variant}
            disableGutters={containerProps?.disableGutters}
            maxWidth={containerMaxWidth}
          >
            <Box display="flex" flexDirection="column" width="100%">
              {children}
            </Box>
          </PageLayoutContainer>
        </Box>
      </PageLayoutRoot>
      {sidebar}
    </>
  )
}

export default PageLayout
